import React, { useState, useEffect, useRef } from "react";
import DOMPurify from "dompurify";
import invoke, { parse90DayRestriction } from "../../common/utils/invoke";
import "./CareerApplicationForm.scss";
import IndiaFlag from "../../assets/images/india-flag.png";
import Modal from "../../common/components/Modal.jsx";
import BrandLoader from "../../common/components/BrandLoader.jsx";

import {
  FILE_SIZE_LIMIT_MB,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
  UI_LABELS,
  HTTP_STATUS,
} from "../../constants";

import {
  validateBySchema,
  validateFieldBySchema,
  careerApplicationSchema,
} from "../../common/utils/validation";

/**
 * Helper function to prepare file for upload with applicant name
 */
const prepareFileForUpload = (file, applicantName) => {
  if (!applicantName || !file) return file;

  try {
    const timestamp = Date.now();
    const ext = file.name.slice(file.name.lastIndexOf("."));
    return new File([file], `${applicantName}_${timestamp}${ext}`, {
      type: file.type,
      lastModified: file.lastModified,
    });
  } catch {
    return file;
  }
};

const initialFormState = {
  name: "",
  mobileNumber: "",
  email: "",
  department: "",
  departmentGroup: "",
  departmentRole: "",
  jobDescription: "",
  resume: null,
};

const CareerApplicationForm = () => {
  const otpRefs = useRef({});
  const OTP_LENGTH = 6;
  const otpSlots = useRef(
    Array.from({ length: OTP_LENGTH }, (_, i) => ({
      id: `otp-slot-${i + 1}`,
      pos: i,
    })),
  );
  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [allreadyAplied, setAllreadyApplied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(true);
  const [departmentsError, setDepartmentsError] = useState(null);

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [apiErrorMsg, setApiErrorMsg] = useState("");
  // OTP States
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [otpId, setOtpId] = useState(null);
  const [otpError, setOtpError] = useState(null);
  const [otpLoading, setOtpLoading] = useState(false);
  const [jobModal, setJobModal] = useState(false);
  const [showNoOpeningsModal, setShowNoOpeningsModal] = useState(false);
  /* ----------------------------------------
   * Fetch Departments
   * --------------------------------------*/
  useEffect(() => {
    const fetchDepartments = async () => {
      setDepartmentsLoading(true);
      setDepartmentsError(null);

      try {
        const response = await invoke.fetchDepartments();
        setDepartments(Array.isArray(response) ? response : []);
      } catch {
        setDepartmentsError(ERROR_MESSAGES.GENERIC_ERROR);
      } finally {
        setDepartmentsLoading(false);
      }
    };

    fetchDepartments();
  }, []);
  
  /* ----------------------------------------
   * Handlers
   * --------------------------------------*/
  const handleChange = (e) => {
    const { name, value } = e.target;

    let sanitizedValue = value;
    if (name === "name") {
      sanitizedValue = value
        .replace(/[^a-zA-Z\s]/g, "") // remove special chars & numbers
        .replace(/\s{2,}/g, " ") // prevent multiple spaces
        .trimStart(); // avoid leading space
    }

    if (name === "mobileNumber") {
      sanitizedValue = value.replace(/\D/g, "").replace(/^0+/, "");
    }

    setFormData((prev) => {
      const updatedForm = {
        ...prev,
        [name]: sanitizedValue,
      };

      const fieldError = validateFieldBySchema(
        name,
        sanitizedValue,
        careerApplicationSchema,
        updatedForm,
      );

      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: fieldError,
      }));

      return updatedForm;
    });
  };

const handleDepartmentChange = (e) => {
  const value = e.target.value;

  //  BLUR DROPDOWN IMMEDIATELY
  e.target.blur();

  const selectedDept = departments.find(
    (d) => d.departmentName === value
  );

  const rawRoles = selectedDept?.departmentRoles || [];

  const validRoles = rawRoles.filter(
    (r) => (r.departmentRole || "").trim() !== ""
  );

  if (value && validRoles.length === 0) {
    setShowNoOpeningsModal(true);

    setFormData((prev) => ({
      ...prev,
      department: "",
      departmentGroup: "",
      departmentRole: "",
      jobDescription: "",
    }));

    setRoles([]);

    return;
  }

  setFormData((prev) => ({
    ...prev,
    department: value,
    departmentGroup:
      selectedDept?.departmentGroup ||
      selectedDept?.department_group ||
      "",
    departmentRole: "",
    jobDescription: "",
  }));

  setRoles(validRoles);

const fieldError = validateFieldBySchema(
  "department",
  value,
  careerApplicationSchema,
  { ...formData, department: value }
);

setErrors((prev) => ({
  ...prev,
  department: fieldError,
}));
};
  const handleRoleChange = (e) => {
    const value = e.target.value;
    const selectedRole = roles.find((r) => r.departmentRole === value);

    setFormData((prev) => ({
      ...prev,
      departmentRole: value,
      jobDescription: selectedRole?.jobDescription || "",
    }));

    const fieldError = validateFieldBySchema(
      "departmentRole",
      value,
      careerApplicationSchema,
      { ...formData, departmentRole: value },
    );

    setErrors((prev) => ({
      ...prev,
      departmentRole: fieldError,
    }));
  };

  const ALLOWED_FILE_TYPES = ["application/pdf", "image/png"];

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;

    // Reset if no file
    if (!file) {
      setFormData((prev) => ({ ...prev, resume: null }));
      setErrors((prev) => ({ ...prev, resume: null }));
      return;
    }
    
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        resume: "Only PDF or PNG files are allowed",
      }));
      e.target.value = "";
      return;
    }

    if (file.size > FILE_SIZE_LIMIT_MB * 1024 * 1024) {
      setErrors((prev) => ({
        ...prev,
        resume: `File size should not exceed ${FILE_SIZE_LIMIT_MB} MB`,
      }));

      // Clear file input
      e.target.value = "";
      setFormData((prev) => ({ ...prev, resume: null }));
      return;
    }

    // ✅ Valid file
    setFormData((prev) => {
      const updatedForm = { ...prev, resume: file };

      const fieldError = validateFieldBySchema(
        "resume",
        file,
        careerApplicationSchema,
        updatedForm,
      );

      setErrors((prevErrors) => ({
        ...prevErrors,
        resume: fieldError,
      }));

      return updatedForm;
    });
  };

  const handleRestResume = () => {
    setFormData((prev) => ({ ...prev, resume: null }));
    setErrors((prev) => ({ ...prev, resume: null }));
  };

  const handleOtpChange = (e) => {
    const value = e.target.value;
    const pos = Number(e.target.dataset.pos);

    if (!/^\d?$/.test(value)) return;

    setOtp((prev) => {
      const updated = [...prev];
      updated[pos] = value;
      return updated;
    });

    // Move to next input if value entered
    if (value) {
      const next = otpRefs.current[pos + 1];
      next?.focus();
    }
  };

  const handleOtpKeyDown = (e) => {
    const pos = Number(e.target.dataset.pos);

    if (e.key === "Backspace" && !otp[pos]) {
      otpRefs.current[pos - 1]?.focus();
    }

    if (e.key === "Enter") {
      const firstEmpty = otp.findIndex((d) => d === "");
      if (firstEmpty !== -1) {
        otpRefs.current[firstEmpty]?.focus();
      }
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();

    const pastedData = e.clipboardData.getData("text");
    const digits = pastedData.replace(/\D/g, "").slice(0, OTP_LENGTH);

    if (!digits) return;

    const updatedOtp = Array(OTP_LENGTH).fill("");

    for (let i = 0; i < digits.length; i++) {
      updatedOtp[i] = digits[i];
    }

    setOtp(updatedOtp);

    // Focus last filled input
    const lastIndex = Math.min(digits.length - 1, OTP_LENGTH - 1);
    otpRefs.current[lastIndex]?.focus();
  };

  /* ----------------------------------------
   * Submit
   * --------------------------------------*/
  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateBySchema(
      formData,
      careerApplicationSchema,
    );

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setSubmitting(true);
    setApiErrorMsg("");
    try {
      const otpResponse = await invoke.generateOTP(formData.email.trim());
      // API returns { message: "OTP generated successfully", status: "success" }; treat status === "success" or 200 as success
      const otpSuccess = otpResponse?.status === "success" || otpResponse?.status === HTTP_STATUS.OK;
      if (!otpSuccess) {
        setApiErrorMsg(otpResponse?.error || otpResponse?.message || ERROR_MESSAGES.OTP_FAILED);
        setShowErrorModal(true);
        return;
      }
      setOtpId(otpResponse?.otpId ?? otpResponse?.id ?? "");
      setShowOtpModal(true);
    } catch (err) {
      setApiErrorMsg(err?.response?.data?.error || ERROR_MESSAGES.OTP_FAILED);
      setShowErrorModal(true);
    } finally {
      setSubmitting(false);
    }
  };

  //  Verify OTP + Final Submit

  const handleVerifyOtp = async () => {
    const enteredOtp = otp.join("");

    if (enteredOtp.length !== 6) {
      setOtpError("Please enter valid 6-digit OTP");
      return;
    }
    try {
      setOtpLoading(true);
      setOtpError(null);

      // VAPT: Send verified snapshot so server locks these fields and rejects modified values on apply
      const verifiedSnapshot = {
        applicantName: formData.name.trim(),
        applicantEmailId: formData.email.trim(),
        applicantMobNo: formData.mobileNumber ? `91${formData.mobileNumber}` : "",
        applicantDepartment: formData.department || "",
        applicantDepartmentGroup: formData.departmentGroup || "",
        applicantDepartmentRole: formData.departmentRole || "",
      };
      const verifyResponse = await invoke.verifyOTP({
        otpId,
        identifier: formData.email.trim(),
        otp: enteredOtp,
        verifiedSnapshot,
      });

      if (verifyResponse?.status !== "success") {
        setOtpError("Invalid OTP. Please try again.");
        return;
      }
      setShowOtpModal(false);
      setOtp(Array(OTP_LENGTH).fill(""));

      // ✅ OTP VERIFIED → NOW submit application (send verification token so apply works when session is not shared, e.g. UAT)
      const multipartFormData = new FormData();

      if (verifyResponse?.verificationToken) {
        multipartFormData.append("otpVerificationToken", verifyResponse.verificationToken);
      }
      multipartFormData.append("applicantName", formData.name.trim());
      multipartFormData.append("applicantEmailId", formData.email.trim());
      // Send mobile as string so backend/Liferay store it correctly (avoids overflow and Excel scientific notation)
      multipartFormData.append(
        "applicantMobNo",
        formData.mobileNumber ? `91${formData.mobileNumber}` : "",
      );
      multipartFormData.append("applicantDepartment", formData.department);
      multipartFormData.append(
        "applicantDepartmentGroup",
        formData.departmentGroup,
      );
      multipartFormData.append(
        "applicantDepartmentRole",
        formData.departmentRole,
      );
      multipartFormData.append(
        "applicantDepartmentJD",
        formData.jobDescription,
      );

      if (formData.resume) {
        const preparedFile = prepareFileForUpload(
          formData.resume,
          formData.name.trim(),
        );
        multipartFormData.append("resume", preparedFile);
      }

      const result =
        await invoke.submitCareerApplicationWithFile(multipartFormData);

      if (parse90DayRestriction(result) || result?.is90DayRestriction) {
        setApiErrorMsg(ERROR_MESSAGES.ALREADY_APPLIED_90_DAYS);
        setShowErrorModal(true);
        setFormData(initialFormState);
        setAllreadyApplied(true);
        return;
      }

      const isSuccess = result && (result === "success" || result.status === "success");
      if (!isSuccess) {
        setApiErrorMsg(result?.error || "Application submission failed");
        setShowErrorModal(true);
        return;
      }

      setShowSuccessModal(true);
      setFormData(initialFormState);
    } catch (err) {
      if (parse90DayRestriction(err?.response?.data)) {
        setApiErrorMsg(ERROR_MESSAGES.ALREADY_APPLIED_90_DAYS);
        setFormData(initialFormState);
        setAllreadyApplied(true);
      } else {
        setApiErrorMsg(err?.response?.data?.error || "Application submission failed");
      }
      setShowErrorModal(true);
    } finally {
      setOtpLoading(false);
    }
  };

  // handle job mobal

  /* ----------------------------------------
   * Render
   * --------------------------------------*/
  return (
    <div className="career-form-wrapper">
      {(otpLoading || submitting) && (
        <div className="loader-overlay">
          <BrandLoader
            color="#97262A"
            loading={otpLoading || submitting}
            size={15}
          />
        </div>
      )}
      <h6 className="small-body semibold">Ready to take the next step?</h6>
      <h5 className="form-title semibold">
        We’re always looking for passionate people who want to make an impact.
      </h5>
      <p className="regular-body regular">
        Apply now and be a part of a workplace that values growth,
        collaboration, and purpose.
      </p>

      <div className="career-form">
        {/* Name */}
        <div className="form-row">
          <label className="label-one">Enter Full Name</label>
          <input
            id="name"
            name="name"
            type="text"
            className={`form-input ${errors.name ? "has-error" : ""}`}
            value={formData.name}
            onChange={handleChange}
            placeholder=""
            autocomplete="off"
          />
          {errors.name && <div className="error-text">{errors.name}</div>}
        </div>

        {/* Mobile */}
        <div className="form-row">
          <label className="label-one">Mobile No.</label>
          <div className="flag-input">
            <div className="flag-box">
              <img className="country-flag" src={IndiaFlag} alt="India" />
              <span className="dial-code">+91</span>
            </div>
            <input
              id="mobileNumber"
              name="mobileNumber"
              pattern="[0-9]*"
              inputMode="numeric"
              type="tel"
              maxLength={10}
              className={`form-input ${errors.mobileNumber ? "has-error" : ""}`}
              value={formData.mobileNumber}
              onChange={handleChange}
              placeholder=""
              autocomplete="off"
            />
          </div>
          {errors.mobileNumber && (
            <div className="error-text">{errors.mobileNumber}</div>
          )}
        </div>

        {/* Email */}
        <div className="form-row">
          <label className="label-one">Email ID</label>
          <input
            id="email"
            name="email"
            type="email"
            className={`form-input ${errors.email ? "has-error" : ""}`}
            value={formData.email}
            onChange={handleChange}
            placeholder=""
            autocomplete="off"
          />

          {errors.email && <div className="error-text">{errors.email}</div>}
        </div>

        {/* Department */}
        <div className="form-row">
          <label className="label-one">Department</label>
          <select
            id="department"
            value={formData.department}
            name="department"
            className={`form-input ${errors.department ? "has-error" : ""}`}
            onChange={handleDepartmentChange}
            disabled={departmentsLoading}
          >
            <option value="">
              {departmentsLoading
                ? UI_LABELS.LOADING_DEPARTMENTS
                : UI_LABELS.SELECT_DEPARTMENT}
            </option>
            {departments.map((dept) => (
              <option key={dept.id} value={dept.departmentName}>
                {dept.departmentName}
              </option>
            ))}
          </select>
          {errors.department && (
            <div className="error-text">{errors.department}</div>
          )}
          {departmentsError && (
            <div className="error-text">{departmentsError}</div>
          )}
        </div>

        {/* Department Role */}
        {roles.length > 0 && (
          <div className="form-row">
            <label className="label-one">Role</label>
            <select
              name="departmentRole"
              value={formData.departmentRole}
              className={`form-input ${
                errors.departmentRole ? "has-error" : ""
              }`}
              onChange={handleRoleChange}
            >
              <option value="">Select Role</option>
              {roles.map((role) => (
                <option key={role.id} value={role.departmentRole}>
                  {role.departmentRole}
                </option>
              ))}
            </select>

            {errors.departmentRole && (
              <div className="error-text">{errors.departmentRole}</div>
            )}
          </div>
        )}

        {formData.jobDescription && (
          <div className="form-row job-description">
            <label class="label-one">&nbsp;</label>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                setJobModal(true);
              }}
            >
              View Job Description
            </button>
            {jobModal && (
              <div className="job-modal-overlay">
                <div className="job-modal-body">
                  <h3>Job Description</h3>
                  <div
                    className="job-description-content"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(formData.jobDescription),
                    }}
                  />
                  <button
                    className="close-btn"
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setJobModal(false);
                    }}
                  >
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 15 15"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M14.781 13.7198C14.8507 13.7895 14.906 13.8722 14.9437 13.9632C14.9814 14.0543 15.0008 14.1519 15.0008 14.2504C15.0008 14.349 14.9814 14.4465 14.9437 14.5376C14.906 14.6286 14.8507 14.7114 14.781 14.781C14.7114 14.8507 14.6286 14.906 14.5376 14.9437C14.4465 14.9814 14.349 15.0008 14.2504 15.0008C14.1519 15.0008 14.0543 14.9814 13.9632 14.9437C13.8722 14.906 13.7895 14.8507 13.7198 14.781L7.50042 8.56073L1.28104 14.781C1.14031 14.9218 0.94944 15.0008 0.750417 15.0008C0.551394 15.0008 0.360523 14.9218 0.219792 14.781C0.0790615 14.6403 3.92322e-09 14.4494 0 14.2504C-3.92322e-09 14.0514 0.0790615 13.8605 0.219792 13.7198L6.4401 7.50042L0.219792 1.28104C0.0790615 1.14031 0 0.94944 0 0.750417C0 0.551394 0.0790615 0.360523 0.219792 0.219792C0.360523 0.0790615 0.551394 0 0.750417 0C0.94944 0 1.14031 0.0790615 1.28104 0.219792L7.50042 6.4401L13.7198 0.219792C13.8605 0.0790615 14.0514 -3.92322e-09 14.2504 0C14.4494 3.92322e-09 14.6403 0.0790615 14.781 0.219792C14.9218 0.360523 15.0008 0.551394 15.0008 0.750417C15.0008 0.94944 14.9218 1.14031 14.781 1.28104L8.56073 7.50042L14.781 13.7198Z"
                        fill="#3C3D3D"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Resume */}
        <div className="form-row w-full">
          <div className="label-top">
            <label className="label-one">Resume</label>
            <p className="file-size-text">
              <span> PNG/ PDF</span>
              <span>
                <svg
                  width="4"
                  height="4"
                  viewBox="0 0 4 4"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1.90442 3.80803C1.55909 3.80803 1.24176 3.72403 0.952422 3.55603C0.663089 3.38803 0.429755 3.15936 0.252422 2.87003C0.0844219 2.5807 0.000421882 2.26336 0.000421882 1.91803C0.000421882 1.56336 0.0844219 1.24136 0.252422 0.952031C0.420422 0.662698 0.649089 0.434031 0.938422 0.266031C1.22776 0.0886975 1.54976 3.0756e-05 1.90442 3.0756e-05C2.25909 3.0756e-05 2.58109 0.0886975 2.87042 0.266031C3.15976 0.434031 3.38842 0.662698 3.55642 0.952031C3.73376 1.24136 3.82242 1.56336 3.82242 1.91803C3.82242 2.26336 3.73376 2.5807 3.55642 2.87003C3.38842 3.15936 3.15976 3.38803 2.87042 3.55603C2.58109 3.72403 2.25909 3.80803 1.90442 3.80803Z"
                    fill="#86878A"
                  />
                </svg>
              </span>
              <span>{UI_LABELS.UPLOAD_RESUME(FILE_SIZE_LIMIT_MB)}</span>
            </p>
          </div>
          <input
            id="resume"
            name="resume"
            type="file"
            className={`hidden-file-input`}
            accept="application/pdf,image/png"
            onChange={handleFileChange}
          />

          <label htmlFor="resume" className="custom-upload-btn">
            <span>
              <svg
                width="36"
                height="43"
                viewBox="0 0 36 43"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M35.2747 11.8503L23.8997 0.475313C23.7487 0.324457 23.5694 0.204835 23.3721 0.123279C23.1749 0.0417232 22.9635 -0.000167352 22.75 5.02447e-07H3.25C2.38805 5.02447e-07 1.5614 0.34241 0.951903 0.951903C0.34241 1.5614 0 2.38805 0 3.25V39C0 39.862 0.34241 40.6886 0.951903 41.2981C1.5614 41.9076 2.38805 42.25 3.25 42.25H32.5C33.362 42.25 34.1886 41.9076 34.7981 41.2981C35.4076 40.6886 35.75 39.862 35.75 39V13C35.7502 12.7865 35.7083 12.5751 35.6267 12.3779C35.5452 12.1806 35.4255 12.0013 35.2747 11.8503ZM24.375 30.875H11.375C10.944 30.875 10.5307 30.7038 10.226 30.399C9.92121 30.0943 9.75 29.681 9.75 29.25C9.75 28.819 9.92121 28.4057 10.226 28.101C10.5307 27.7962 10.944 27.625 11.375 27.625H24.375C24.806 27.625 25.2193 27.7962 25.524 28.101C25.8288 28.4057 26 28.819 26 29.25C26 29.681 25.8288 30.0943 25.524 30.399C25.2193 30.7038 24.806 30.875 24.375 30.875ZM24.375 24.375H11.375C10.944 24.375 10.5307 24.2038 10.226 23.899C9.92121 23.5943 9.75 23.181 9.75 22.75C9.75 22.319 9.92121 21.9057 10.226 21.601C10.5307 21.2962 10.944 21.125 11.375 21.125H24.375C24.806 21.125 25.2193 21.2962 25.524 21.601C25.8288 21.9057 26 22.319 26 22.75C26 23.181 25.8288 23.5943 25.524 23.899C25.2193 24.2038 24.806 24.375 24.375 24.375ZM22.75 13V4.0625L31.6875 13H22.75Z"
                  fill="#AFB0B1"
                />
              </svg>
            </span>
            {formData.resume && (
              <>
                <span className="file-name">{formData.resume.name}</span>
                <span className="delete-resume" onClick={handleRestResume}>
                  <svg
                    width="16"
                    height="18"
                    viewBox="0 0 16 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M14.6875 3.125H11.875V2.1875C11.875 1.60734 11.6445 1.05094 11.2343 0.640704C10.8241 0.230468 10.2677 0 9.6875 0H5.9375C5.35734 0 4.80094 0.230468 4.3907 0.640704C3.98047 1.05094 3.75 1.60734 3.75 2.1875V3.125H0.9375C0.68886 3.125 0.450403 3.22377 0.274587 3.39959C0.0987721 3.5754 0 3.81386 0 4.0625C0 4.31114 0.0987721 4.5496 0.274587 4.72541C0.450403 4.90123 0.68886 5 0.9375 5H1.25V15.625C1.25 16.0394 1.41462 16.4368 1.70765 16.7299C2.00067 17.0229 2.3981 17.1875 2.8125 17.1875H12.8125C13.2269 17.1875 13.6243 17.0229 13.9174 16.7299C14.2104 16.4368 14.375 16.0394 14.375 15.625V5H14.6875C14.9361 5 15.1746 4.90123 15.3504 4.72541C15.5262 4.5496 15.625 4.31114 15.625 4.0625C15.625 3.81386 15.5262 3.5754 15.3504 3.39959C15.1746 3.22377 14.9361 3.125 14.6875 3.125ZM5.625 2.1875C5.625 2.10462 5.65792 2.02513 5.71653 1.96653C5.77513 1.90792 5.85462 1.875 5.9375 1.875H9.6875C9.77038 1.875 9.84987 1.90792 9.90847 1.96653C9.96708 2.02513 10 2.10462 10 2.1875V3.125H5.625V2.1875ZM12.5 15.3125H3.125V5H12.5V15.3125ZM6.875 7.5V12.5C6.875 12.7486 6.77623 12.9871 6.60041 13.1629C6.4246 13.3387 6.18614 13.4375 5.9375 13.4375C5.68886 13.4375 5.4504 13.3387 5.27459 13.1629C5.09877 12.9871 5 12.7486 5 12.5V7.5C5 7.25136 5.09877 7.0129 5.27459 6.83709C5.4504 6.66127 5.68886 6.5625 5.9375 6.5625C6.18614 6.5625 6.4246 6.66127 6.60041 6.83709C6.77623 7.0129 6.875 7.25136 6.875 7.5ZM10.625 7.5V12.5C10.625 12.7486 10.5262 12.9871 10.3504 13.1629C10.1746 13.3387 9.93614 13.4375 9.6875 13.4375C9.43886 13.4375 9.2004 13.3387 9.02459 13.1629C8.84877 12.9871 8.75 12.7486 8.75 12.5V7.5C8.75 7.25136 8.84877 7.0129 9.02459 6.83709C9.2004 6.66127 9.43886 6.5625 9.6875 6.5625C9.93614 6.5625 10.1746 6.66127 10.3504 6.83709C10.5262 7.0129 10.625 7.25136 10.625 7.5Z"
                      fill="#DA1414"
                    />
                  </svg>
                </span>
              </>
            )}
          </label>

          {errors.resume && <div className="error-text">{errors.resume}</div>}
        </div>

        <div className="form-actions">
          <button
            className="submit-button btn primary-button regular "
            type="submit"
            disabled={submitting}
            onClick={handleSubmit}
          >
            {submitting ? UI_LABELS.SUBMITTING : UI_LABELS.SUBMIT}
          </button>
        </div>
      </div>

      {showOtpModal && (
        <div className="otp-modal-backdrop">
          <div className="otp-modal">
            <h6 className="large-body">Verify OTP</h6>
            <p className="small-body">
              Enter the 6-digit OTP sent to your email
            </p>

            <div className="otp-inputs">
              {otpSlots.current.map(({ id, pos }) => (
                <input
                  key={id}
                  ref={(el) => (otpRefs.current[pos] = el)}
                  data-pos={pos}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={otp[pos]}
                  disabled={otpLoading}
                  onChange={handleOtpChange}
                  onKeyDown={handleOtpKeyDown}
                  onPaste={handleOtpPaste}
                />
              ))}
            </div>

            {otpError && <div className="error-text">{otpError}</div>}

            <div className="otp-actions">
              <button
                className="modal-close"
                onClick={() => setShowOtpModal(false)}
              >
                ✕
              </button>
              <button
                className="modal-btn btn primary-button regular"
                onClick={handleVerifyOtp}
                disabled={otpLoading}
              >
                {otpLoading ? "Verifying..." : "Verify"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SUCCESS MODAL */}
      <Modal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          setFormData(initialFormState);
          setApiErrorMsg("");
          setErrors("");
        }}
        title="You’re all set!"
        type="success"
        btnTitle="Done"
      >
        <p>{SUCCESS_MESSAGES.FORM_SUBMITTED}</p>
      </Modal>
      {/* ERROR MODAL */}
      <Modal
        isOpen={showErrorModal}
        onClose={() => {
          setShowErrorModal(false);
          setFormData(initialFormState);
          setApiErrorMsg("");
          setErrors("");
          setShowOtpModal(false);
        }}
        title={`${
          allreadyAplied ? "Already applied" : "Something went wrong!"
        }`}
        type="error"
        btnTitle="Try Again"
      >
        <p>{apiErrorMsg}</p>
      </Modal>

      {/* NO OPENINGS MODAL - department has no roles */}
      <Modal
        isOpen={showNoOpeningsModal}
        onClose={() => setShowNoOpeningsModal(false)}
        title=""
        type="error"
        btnTitle="OK"
      >
        <p>{UI_LABELS.NO_OPENINGS_MODAL_MESSAGE}</p>
      </Modal>
    </div>
  );
};

export default CareerApplicationForm;
